import React, { useEffect, useState } from "react";
import {
  Button,
  CircularProgress,
  Grid,
  TextField,
  Typography,
} from "@mui/material";
import moment from "moment";
import {
  blockInvalidCharFromNumberInput,
  numberInputOnWheelPreventChange,
} from "../../../../Utils/ConversionUtils";
import PropTypes from "prop-types";
import {
  assignToCopart,
  assignToIAA,
} from "../../../../Utils/SalvageBookingsUtils";
import { useNotify, useRecordContext, useRefresh } from "react-admin";
import { getRulesSetsByRuleType } from "../../../../Utils/RestUtils";

const Reserve = ({ sb, setPopupPanelName }) => {
  const [loading, setLoading] = useState(false);
  const [reserveValidationError, setReserveValidationError] = useState(null);
  const [reserve, setReserve] = useState("");
  const [showIAA, setShowIAA] = useState(false);
  const [auctioneerAssignmentNotes, setAuctioneerAssignmentNotes] = useState(
    "EXCHANGE CHECK FOR SIGNED AND FULLY TRANSFERABLE TITLE, WITH KEYS AND VEHICLE. MAKE SURE VIN ON PAPERWORK MATCHES VIN ON VEHICLE AND THAT LOT RUNS AND DRIVES (IF RELEVANT). ONLY PAY THE SPECIFIC AMOUNT AGREED BY SELLER."
  );
  const record = useRecordContext();
  const refresh = useRefresh();
  const notify = useNotify();

  useEffect(() => {
    if (!record?.id) return;
    getRulesSetsByRuleType(record.id, "iaa_booking").then(({ data }) => {
      setShowIAA(Array.isArray(data) && data.length > 0);
    });
  }, [record?.id]);

  const handleAuctioneerAssignmentNotesChange = (event) => {
    setAuctioneerAssignmentNotes(event.target.value);
  };

  const handleReserveChange = (event) => {
    setReserveValidationError("");
    if (event.target.value <= 0) {
      setReserveValidationError("Reserve must be more than 0");
    }

    setReserve(event.target.value);
  };

  const handleAssignToCopartButtonClick = async () => {
    setLoading(true);

    if (reserve === "") {
      setReserveValidationError("Reserve must be more than 1");
      setLoading(false);
      return;
    }

    // UNCOMMENT THIS LINE AND REMOVE assignToCopart & responseStatus if
    // statement BELOW TO ENABLE PREMIUM LEAD POPUP CHOICE
    // setPopupPanelName("premiumLead");

    // Update send review status in salvage bookings
    const responseStatus = await assignToCopart(
      parseInt(record.originId),
      "false",
      document.querySelector('[id="reserve-assign-copart"]').value,
      document.querySelector('[id="auctioneer-assignment-notes"]').value
    );

    if (responseStatus) {
      if (!responseStatus.copartError) {
        notify("Assigned to Copart successfully", { type: "success" });
        refresh();
      } else {
        setLoading(false);
        notify(" " + responseStatus.copartError, { type: "error" });
      }
    } else {
      setLoading(false);
      notify("Something went wrong, please try again", { type: "error" });
    }
  };

  const handleAssignToIAAButtonClick = async () => {
    setLoading(true);

    if (reserve === "") {
      setReserveValidationError("Reserve must be more than 1");
      setLoading(false);
      return;
    }

    // Update send review status in salvage bookings
    const responseStatus = await assignToIAA(
      parseInt(record.originId),
      document.querySelector('[id="reserve-assign-copart"]').value
    );

    if (responseStatus) {
      if (!responseStatus.copartError) {
        notify("Assigned to IAA successfully", { type: "success" });
        refresh();
      } else {
        setLoading(false);
        notify(" " + responseStatus.copartError, { type: "error" });
      }
    } else {
      setLoading(false);
      notify("Something went wrong, please try again", { type: "error" });
    }
  };

  return (
    <Grid
      container
      xs={12}
      sx={{
        display: "flex",
        alignItems: "flex-start",
        marginY: 3,
      }}
    >
      {sb && sb.quarantined ? (
        <Typography
          sx={{
            fontWeight: "700",
            lineHeight: "26px",
            letterSpacing: "0.46px",
            textTransform: "capitalize",
          }}
        >
          Lead is currently quarantined, unquarantine to proceed
        </Typography>
      ) : !sb || sb.assigned_to_copart_at == null ? (
        <React.Fragment>
          <Grid item xs={3} sx={{ paddingRight: 3 }}>
            <TextField
              id="auctioneer-assignment-notes"
              label="Auctioneer Assignment Notes"
              variant="outlined"
              type="text"
              multiline={true}
              value={auctioneerAssignmentNotes}
              onChange={handleAuctioneerAssignmentNotesChange}
              sx={{
                "& .MuiInputBase-root": {
                  marginBottom: 0,
                  minHeight: "40px",
                  padding: 1,
                },
              }}
            />
          </Grid>
          <Grid item xs={3} sx={{ paddingRight: 3 }}>
            <TextField
              id="reserve-assign-copart"
              label="Reserve"
              variant="outlined"
              type="number"
              value={reserve}
              onWheel={numberInputOnWheelPreventChange}
              onKeyDown={blockInvalidCharFromNumberInput}
              onChange={handleReserveChange}
              error={reserveValidationError}
              helperText={reserveValidationError ?? " "}
              sx={{
                "& .MuiInputBase-root": {
                  marginBottom: 0,
                  height: "40px",
                  minHeight: "40px",
                },
              }}
            />
          </Grid>
          {!showIAA && (
            <Grid item xs={3}>
              <Button
                variant="contained"
                color="secondary"
                label="Assign to copart"
                fullWidth
                disabled={loading}
                onClick={handleAssignToCopartButtonClick}
                sx={{
                  border: "2px solid #FFF !important",
                  backgroundColor: "#13A310",
                  color: "#FFF",
                  height: "40px",
                  fontWeight: "700",
                  textTransform: "uppercase",
                  "&:hover": {
                    backgroundColor: "#13A310",
                  },
                }}
              >
                {loading ? (
                  <CircularProgress
                    size={24}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      marginTop: "-12px",
                      marginLeft: "-12px",
                    }}
                  />
                ) : (
                  <>Assign to copart</>
                )}
              </Button>
            </Grid>
          )}
          {showIAA && (
            <Grid item xs={3}>
              <Button
                variant="contained"
                color="secondary"
                label="Assign to IAA"
                fullWidth
                disabled={loading}
                onClick={handleAssignToIAAButtonClick}
                sx={{
                  border: "2px solid #FFF !important",
                  backgroundColor: "#13A310",
                  color: "#FFF",
                  height: "40px",
                  fontWeight: "700",
                  textTransform: "uppercase",
                  "&:hover": {
                    backgroundColor: "#13A310",
                  },
                }}
              >
                {loading ? (
                  <CircularProgress
                    size={24}
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "50%",
                      marginTop: "-12px",
                      marginLeft: "-12px",
                    }}
                  />
                ) : (
                  <>Assign to IAA</>
                )}
              </Button>
            </Grid>
          )}
        </React.Fragment>
      ) : sb && sb.reserve != null ? (
        <React.Fragment>
          <Typography
            sx={{
              color: "#13A310",
              fontWeight: "700",
              lineHeight: "26px",
              letterSpacing: "0.46px",
              marginRight: "5px",
              textTransform: "uppercase",
            }}
          >
            Assigned:{" "}
          </Typography>
          <Typography
            sx={{
              fontWeight: "700",
              letterSpacing: "0.46px",
              lineHeight: "26px",
              textTransform: "uppercase",
            }}
          >
            {new Intl.NumberFormat("en-US", {
              style: "currency",
              currency: "USD",
            }).format(sb.reserve)}{" "}
            Reserve
          </Typography>
          <Typography
            sx={{
              fontWeight: "400",
              lineHeight: "26px",
              letterSpacing: "0.46px",
              marginLeft: 1,
            }}
          >
            {moment(sb.assigned_to_copart_at).format("DD/MM/YYYY HH:mm")}
          </Typography>
        </React.Fragment>
      ) : (
        <React.Fragment>
          {sb == null && (
            <Typography sx={{ fontWeight: "700" }}>
              Reserve Needs to be Set
            </Typography>
          )}
        </React.Fragment>
      )}
    </Grid>
  );
};

Reserve.propTypes = {
  sb: PropTypes.object,
  setPopupPanelName: PropTypes.func,
};

export default Reserve;
